import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, AlertCircle, Home, Calendar } from "lucide-react";
import { API, usePublicConfig } from "../App";
import { firePurchaseConversion } from "../lib/analytics";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function PaymentSuccessPage() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const quoteId = search.get("quote_id");
  const [status, setStatus] = useState("polling"); // polling | paid | unpaid | expired | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);
  const publicConfig = usePublicConfig();
  const conversionFiredRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let alive = true;

    const poll = async () => {
      if (!alive) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        const data = res.data;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid"); return;
        }
        if (data.status === "expired") {
          setStatus("expired"); return;
        }
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus("unpaid"); return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus("error");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  // Fire Google Ads conversion + GA4 purchase event exactly ONCE when paid.
  useEffect(() => {
    if (status !== "paid") return;
    if (conversionFiredRef.current) return;
    if (!info || info.amount_total == null) return;
    conversionFiredRef.current = true;
    firePurchaseConversion({
      transactionId: quoteId || info.session_id || sessionId,
      value: Number(info.amount_total) / 100,
      currency: (info.currency || "eur").toUpperCase(),
      adsConversionId: publicConfig.google_ads_conversion_id,
      adsConversionLabel: publicConfig.google_ads_conversion_label,
      routeName: info.route_name || undefined,
    });
  }, [status, info, quoteId, sessionId, publicConfig.google_ads_conversion_id, publicConfig.google_ads_conversion_label]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center" data-testid="pay-success-card">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[#C5A059] mx-auto mb-3 animate-spin" />
            <h1 className="text-white text-xl font-semibold mb-2" data-testid="pay-status-polling">Confirming your payment&hellip;</h1>
            <p className="text-white/50 text-sm">This usually takes a few seconds. Please don&rsquo;t close this page.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-white text-2xl font-bold mb-2" data-testid="pay-status-paid">Payment received!</h1>
            <p className="text-white/60 text-sm mb-6">
              Your booking <strong className="text-white">{quoteId}</strong> is now <strong className="text-emerald-400">confirmed</strong>.
              We&rsquo;ve emailed you a receipt and the full booking details.
            </p>
            {info?.amount_total != null && (
              <p className="text-white/40 text-xs mb-6 font-mono">€{(info.amount_total / 100).toFixed(2)} {info.currency?.toUpperCase()}</p>
            )}
            <div className="flex gap-2 justify-center">
              <Link to="/dashboard" className="bg-[#C5A059] hover:bg-[#B8903F] text-black text-sm px-4 py-2 rounded-sm flex items-center gap-1" data-testid="pay-success-dashboard">
                <Calendar className="w-4 h-4" /> My bookings
              </Link>
              <Link to="/" className="bg-white/5 hover:bg-white/10 text-white text-sm px-4 py-2 rounded-sm flex items-center gap-1">
                <Home className="w-4 h-4" /> Home
              </Link>
            </div>
          </>
        )}
        {status === "unpaid" && (
          <>
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2" data-testid="pay-status-unpaid">Still processing&hellip;</h1>
            <p className="text-white/50 text-sm mb-4">
              Your bank is taking a little longer than usual. You&rsquo;ll receive a confirmation email as soon as the payment clears.
            </p>
            <Link to="/dashboard" className="text-[#C5A059] hover:underline text-sm">Open my dashboard &rarr;</Link>
          </>
        )}
        {status === "expired" && (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2" data-testid="pay-status-expired">Payment session expired</h1>
            <p className="text-white/50 text-sm mb-4">No worries — you can start a fresh payment from the link below.</p>
            {quoteId && <Link to={`/pay/${quoteId}`} className="text-[#C5A059] hover:underline text-sm">Try again &rarr;</Link>}
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2" data-testid="pay-status-error">Something went wrong</h1>
            <p className="text-white/50 text-sm mb-4">We couldn&rsquo;t verify your payment status. If you completed the payment, we&rsquo;ll process it shortly via webhook.</p>
            <Link to="/dashboard" className="text-[#C5A059] hover:underline text-sm">Open my dashboard &rarr;</Link>
          </>
        )}
      </div>
    </div>
  );
}
